import { useState, useEffect } from "react";
import { Clock, AlertTriangle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

interface CountdownTimerProps {
  deadline: string;
  onExpired: () => void;
}

export const CountdownTimer = ({ deadline, onExpired }: CountdownTimerProps) => {
  const [timeLeft, setTimeLeft] = useState<{
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
    isExpired: boolean;
  }>({ days: 0, hours: 0, minutes: 0, seconds: 0, isExpired: false });

  useEffect(() => {
    const updateTimer = () => {
      const now = new Date().getTime();
      const deadlineTime = new Date(deadline).getTime();
      const difference = deadlineTime - now;

      if (difference <= 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0, isExpired: true });
        onExpired();
        return;
      }

      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      setTimeLeft({ days, hours, minutes, seconds, isExpired: false });
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [deadline, onExpired]);

  const isUrgent = timeLeft.days === 0 && timeLeft.hours < 2;

  if (timeLeft.isExpired) {
    return (
      <Card className="border-destructive/20 bg-destructive/5">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-destructive" />
            </div>
            <div>
              <p className="font-semibold text-destructive">Voting Closed</p>
              <p className="text-sm text-destructive/80">The deadline has passed</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`${isUrgent ? 'border-warning/20 bg-warning/5' : 'border-primary/20 bg-primary/5'} shadow-glass`}>
      <CardContent className="p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className={`w-10 h-10 rounded-full ${isUrgent ? 'bg-warning/10' : 'bg-primary/10'} flex items-center justify-center`}>
            <Clock className={`w-5 h-5 ${isUrgent ? 'text-warning' : 'text-primary'}`} />
          </div>
          <div>
            <p className="font-semibold">Voting Deadline</p>
            <p className="text-sm text-muted-foreground">Time remaining to vote</p>
          </div>
          {isUrgent && (
            <Badge variant="destructive" className="ml-auto animate-pulse">
              Urgent
            </Badge>
          )}
        </div>
        
        <div className="grid grid-cols-4 gap-2">
          <div className="text-center">
            <div className={`text-2xl font-bold ${isUrgent ? 'text-warning' : 'text-primary'}`}>
              {timeLeft.days}
            </div>
            <div className="text-xs text-muted-foreground">Days</div>
          </div>
          <div className="text-center">
            <div className={`text-2xl font-bold ${isUrgent ? 'text-warning' : 'text-primary'}`}>
              {timeLeft.hours}
            </div>
            <div className="text-xs text-muted-foreground">Hours</div>
          </div>
          <div className="text-center">
            <div className={`text-2xl font-bold ${isUrgent ? 'text-warning' : 'text-primary'}`}>
              {timeLeft.minutes}
            </div>
            <div className="text-xs text-muted-foreground">Minutes</div>
          </div>
          <div className="text-center">
            <div className={`text-2xl font-bold ${isUrgent ? 'text-warning' : 'text-primary'}`}>
              {timeLeft.seconds}
            </div>
            <div className="text-xs text-muted-foreground">Seconds</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};